import * as React from 'react';
import { useMount } from 'react-use';
import { flatten, isEmpty } from 'lodash';

import { findBellowByType } from '../../figma/DolEditor/helpers/nodeValue';
import { CORRECT_ANSWERS } from '../../figma/BlogEditor/plugins-common/CorrectAnswers/types';
import { EXERCISE_MULTIPLE_CHOICE, EXERCISE_MULTIPLE_CHOICE_QUESTION } from '../multiple-choice/types';
import { EXERCISE_GAP_FILL } from '../gap-fill/types';
import { EXERCISE_GAP_FILL_WITH_HINT } from '../gap-fill-with-hint/types';
import { EXERCISE_SHORT_ANSWER } from '../short-answer/types';
import { EXERCISE_QUIZ } from '../quiz/types';
import { EditorExerciseMultipleChoicePlugin } from '../multiple-choice/EditorExerciseMultipleChoicePlugin';
import { EditorExerciseGapFillPlugin } from '../gap-fill/EditorExerciseGapFillPlugin';
import { EditorExerciseGapFillWithHintPlugin } from '../gap-fill-with-hint/EditorExerciseGapFillWithHintPlugin';
import { EditorExerciseShortAnswerPlugin } from '../short-answer/EditorExerciseShortAnswerPlugin';
import { EditorExerciseQuizPlugin } from '../quiz/EditorExerciseQuizPlugin';

const exerciseTypeList = [
  EXERCISE_MULTIPLE_CHOICE,
  EXERCISE_GAP_FILL,
  EXERCISE_GAP_FILL_WITH_HINT,
  EXERCISE_SHORT_ANSWER,
  EXERCISE_QUIZ,
];

interface BlogEditorExercisePluginsProps {
  progressData?: any[];
  initialValue?: any;
  onCheckAnswer?: (payload?: any, isCompleted?: boolean) => void;
}

export const useBlogEditorExercisePlugins = (props?: BlogEditorExercisePluginsProps) => {
  const { progressData = [], initialValue, onCheckAnswer } = props;
  const [progressList, setProgressList] = React.useState(progressData || []);

  useMount(() => {
    if (!isEmpty(progressData)) {
      setProgressList(progressData);
    }
  });

  const listExerciseType = initialValue?.filter(item => {
    return exerciseTypeList?.includes(item?.type);
  });

  const totalExercises = listExerciseType?.reduce((total, eachExerciseType) => {
    const questionCount = findBellowByType(eachExerciseType, EXERCISE_MULTIPLE_CHOICE_QUESTION);
    const questionCount2 = flatten(
      findBellowByType(eachExerciseType, CORRECT_ANSWERS).map((ans) => ans.children),
    );
    const questionCount3 = findBellowByType(eachExerciseType, 'exercise-quiz-questions');
    return total + questionCount.length + questionCount2.length + questionCount3.length;
  }, 0);

  const handleCheckLastAnswer = (payload) => {
    let isCompleted = false;
    const totalExerciseTypes = listExerciseType?.length;
    const totalAnswers = progressList?.length;
    if (!progressList?.find(item => item?.exerciseId === payload?.exerciseId)) {
      if (totalAnswers + 1 === totalExerciseTypes) {
        isCompleted = true;
      }
    }
    setProgressList(prevProgressList => {
      if (prevProgressList?.find(item => item?.exerciseId === payload?.exerciseId)) {
        return prevProgressList?.map(obj => {
          if (payload?.exerciseId === obj?.questionId) {
            return payload;
          }
          return obj;
        });
      }
      return [...prevProgressList, payload];
    });
    return isCompleted;
  };

  const handleCheckAnswer = (payload) => {
    const isCompleted = handleCheckLastAnswer(payload);
    onCheckAnswer?.({ ...payload, totalExercises }, isCompleted);
  };

  return {
    exercisePlugins: [
      EditorExerciseMultipleChoicePlugin({
        isReadOnly: true,
        progressList: progressData || [],
        onCheckAnswer: (payload) => handleCheckAnswer(payload),
      }),
      EditorExerciseGapFillWithHintPlugin({
        isReadOnly: true,
        progressList: progressData || [],
        onCheckAnswer: (payload) => handleCheckAnswer(payload),
      }),
      EditorExerciseQuizPlugin({
        isReadOnly: true,
        progressList: progressData || [],
        onCheckAnswer: (payload) => handleCheckAnswer(payload),
      }),
      EditorExerciseShortAnswerPlugin({
        isReadOnly: true,
        progressList: progressData || [],
        onCheckAnswer: (payload) => handleCheckAnswer(payload),
      }),
      EditorExerciseGapFillPlugin({
        isReadOnly: true,
        progressList: progressData || [],
        onCheckAnswer: (payload) => handleCheckAnswer(payload),
      }),
    ],
  };
};
